import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Account,
  AccountType,
  InvestmentPackage,
  Money as MoneyModel,
  PackageStats,
} from 'api-client';
import { PlusIcon } from 'components/common/icons';
import { ComfortAccountTopupModal } from 'components/common/modals';
import { createAccountManager, createInvestmentPackageManager } from 'di';
import { useErrorCallback, useProfileData } from 'hooks';
import { cn } from 'utils';

import styles from '../AccountDetails.module.scss';
import { AccountDetailsBreadcrumbs } from '../shared/AccountDetailsBreadcrumbs';
import { AccountDetailsLoading } from '../shared/AccountDetailsLoading';
import { AccountHeaderCard } from '../shared/AccountHeaderCard';
import { AccountStatsCard } from '../shared/AccountStatsCard';
import { mapPackageToComfortRow } from '../shared/comfortPackageRows';
import { ComfortPackagesTable } from '../shared/ComfortPackagesTable';
import { useComfortAccountStats } from '../shared/useComfortAccountStats';

const TITLE = 'Счет комфорт';

export const ComfortAccountDetails: FC = () => {
  const profile = useProfileData();
  const handleError = useErrorCallback();
  const handleErrorRef = useRef(handleError);

  const [account, setAccount] = useState<Account | null>(null);
  const [mainAccount, setMainAccount] = useState<Account | null>(null);
  const [packages, setPackages] = useState<InvestmentPackage[]>([]);
  const [packageStats, setPackageStats] = useState<PackageStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const stats = useComfortAccountStats(packages, packageStats);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const accountManager = await createAccountManager();
        const [accountData, mainAccountData] = await Promise.all([
          accountManager.getOne(AccountType.COMFORT),
          accountManager.getOne(AccountType.MAIN),
        ]);

        if (cancelled) return;

        setAccount(accountData);
        setMainAccount(mainAccountData);

        if (accountData) {
          const packageManager = await createInvestmentPackageManager();
          const [packageList, statsList] = await Promise.all([
            packageManager.getAll(),
            packageManager.getPackageStats(),
          ]);
          if (!cancelled) {
            setPackages(packageList);
            setPackageStats(statsList);
          }
        } else if (!cancelled) {
          setPackages([]);
          setPackageStats([]);
        }
      } catch (error) {
        if (!cancelled && error instanceof Error) {
          await handleErrorRef.current(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const headerMoney = useMemo(
    () => account?.balance ?? new MoneyModel(0, profile.balance.currencyCode),
    [account, profile.balance.currencyCode]
  );

  const packageRows = useMemo(() => {
    const statsByPackage = new Map(packageStats.map((item) => [item.packageUuid, item]));
    return packages.map((pkg) => mapPackageToComfortRow(pkg, statsByPackage.get(pkg.uuid)));
  }, [packages, packageStats]);

  const availableOnMainAccount = useMemo(
    () => mainAccount?.balance ?? new MoneyModel(0, profile.balance.currencyCode),
    [mainAccount, profile.balance.currencyCode]
  );

  const reloadAccountData = async () => {
    const accountManager = await createAccountManager();
    const [accountData, mainAccountData] = await Promise.all([
      accountManager.getOne(AccountType.COMFORT),
      accountManager.getOne(AccountType.MAIN),
    ]);
    setAccount(accountData);
    setMainAccount(mainAccountData);
    if (accountData) {
      const packageManager = await createInvestmentPackageManager();
      const [packageList, statsList] = await Promise.all([
        packageManager.getAll(),
        packageManager.getPackageStats(),
      ]);
      setPackages(packageList);
      setPackageStats(statsList);
    }
  };

  if (loading) {
    return <AccountDetailsLoading />;
  }

  if (!account) {
    return (
      <div className={styles.container}>
        <AccountDetailsBreadcrumbs title={TITLE} />
        <div className={styles.emptyState}>
          <p>Счет комфорт еще не открыт</p>
          <Link to="../.." relative="path" className={styles.breadcrumbLink}>
            Вернуться на главную
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AccountDetailsBreadcrumbs title={TITLE} />

      <AccountHeaderCard
        money={headerMoney}
        label="счет комфорт"
        actions={
          <button
            type="button"
            className={cn(styles.actionButton, styles.actionButtonHalf)}
            onClick={() => setTopupModalOpen(true)}
          >
            <div className={styles.actionIcon}>
              <PlusIcon preset="circle-filled" size={40} />
            </div>
            <span className={styles.actionText}>
              Создать новый
              <br />
              пакет
            </span>
          </button>
        }
      />

      <AccountStatsCard stats={stats} />

      <ComfortPackagesTable rows={packageRows} emptyMessage="Нет инвестиционных пакетов" />

      <ComfortAccountTopupModal
        active={topupModalOpen}
        onClose={() => setTopupModalOpen(false)}
        availableOnMainAccount={availableOnMainAccount}
        onSuccess={() => void reloadAccountData()}
      />
    </div>
  );
};
